import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Clock, DownloadSimple, CaretDown, CaretRight, Warning, Timer, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "../DateInput";
const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
const PRESETS = {
  this_week: () => { const s = startOfWeek(new Date()); const e = new Date(s); e.setDate(s.getDate() + 6); return [iso(s), iso(e)]; },
  last_week: () => { const s = startOfWeek(new Date()); s.setDate(s.getDate() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return [iso(s), iso(e)]; },
  this_month: () => { const n = new Date(); return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(new Date(n.getFullYear(), n.getMonth() + 1, 0))]; },
  last_month: () => { const n = new Date(); return [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), iso(new Date(n.getFullYear(), n.getMonth(), 0))]; },
};
const hrs = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;

/* Per-engineer hours from arrive → complete stamps. `endpoint` = /supplier-portal/team/timesheet or /suppliers/{id}/timesheet.
   `jobLinkBase` decides where job refs link (supplier portal vs admin). */
export default function SupplierTimesheetPanel({ endpoint, jobLinkBase = "/supplier-portal", testId = "supplier-timesheet" }) {
  const [preset, setPreset] = useState("this_week");
  const [range, setRange] = useState(PRESETS.this_week());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await API.get(endpoint, { params: { start: range[0], end: range[1] } });
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load timesheet"); }
    finally { setLoading(false); }
  }, [endpoint, range]);
  useEffect(() => { load(); }, [load]);

  const pick = (id) => { setPreset(id); setRange(PRESETS[id]()); };
  const exportCsv = async () => {
    try {
      const res = await API.get(endpoint, { params: { start: range[0], end: range[1], format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `timesheet_${range[0]}_${range[1]}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't export CSV"); }
  };
  const jobLink = (r) => `${jobLinkBase}/${r.entity_type === "emergency_job" ? (jobLinkBase === "/supplier-portal" ? "emergency" : "emergency-jobs") : "jobs"}/${r.entity_id}`;

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Timer size={20} className="text-primary" /> Timesheet</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Hours per engineer from their Arrive → Complete stamps on site{data?.supplier ? ` · ${data.supplier}` : ""}. Use it to reconcile invoices against time actually on site.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.entries} data-testid={`${testId}-export`}><DownloadSimple size={14} className="mr-1" /> Export CSV</Button>
      </div>
      <div className="flex items-center gap-2 flex-wrap" data-testid={`${testId}-presets`}>
        {[["this_week", "This week"], ["last_week", "Last week"], ["this_month", "This month"], ["last_month", "Last month"]].map(([id, label]) => (
          <button key={id} onClick={() => pick(id)} data-testid={`${testId}-preset-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${preset === id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{label}</button>
        ))}
        <div className="flex items-center gap-1 ml-auto">
          <DateInput value={range[0]} onChange={(e) => { setPreset("custom"); setRange([e.target.value, range[1]]); }} className="h-8 w-36 text-xs" data-testid={`${testId}-start`} />
          <span className="text-xs text-muted-foreground">to</span>
          <DateInput value={range[1]} onChange={(e) => { setPreset("custom"); setRange([range[0], e.target.value]); }} className="h-8 w-36 text-xs" data-testid={`${testId}-end`} />
        </div>
      </div>

      {loading && !data ? <p className="text-sm text-muted-foreground">Loading…</p> : !data?.engineers?.length ? (
        <div className="border border-dashed border-border rounded-lg p-6 text-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>
          No on-site time recorded between {range[0]} and {range[1]}. Hours appear once an engineer taps Arrive on site.
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Engineer</th><th className="text-right px-3 py-2">Jobs</th><th className="text-right px-3 py-2">Days</th><th className="text-right px-3 py-2">Hours</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {data.engineers.map((e) => (
                <EngineerRows key={e.user_id} e={e} open={!!open[e.user_id]} toggle={() => setOpen((o) => ({ ...o, [e.user_id]: !o[e.user_id] }))} jobLink={jobLink} testId={testId} />
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-semibold">
              <tr data-testid={`${testId}-total`}>
                <td className="px-3 py-2">Total · {data.engineers.length} engineer{data.engineers.length === 1 ? "" : "s"}</td>
                <td className="px-3 py-2 text-right">{data.entries}</td>
                <td className="px-3 py-2 text-right">—</td>
                <td className="px-3 py-2 text-right font-mono">{hrs(data.grand_total_minutes)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function EngineerRows({ e, open, toggle, jobLink, testId }) {
  return (
    <>
      <tr className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={toggle} data-testid={`${testId}-engineer-${e.user_id}`}>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
            <span className="font-medium">{e.name}</span>
            {e.trade && <span className="text-[11px] text-muted-foreground">{e.trade}</span>}
            {e.off_site > 0 && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700" title="Arrivals flagged off-site"><Warning size={10} weight="fill" /> {e.off_site} off-site</span>}
            {e.open > 0 && <Badge className="text-[9px] bg-blue-500/10 text-blue-700 hover:bg-blue-500/10">{e.open} still on site</Badge>}
          </div>
        </td>
        <td className="px-3 py-2 text-right">{e.jobs}</td>
        <td className="px-3 py-2 text-right">{e.days}</td>
        <td className="px-3 py-2 text-right font-mono" data-testid={`${testId}-engineer-hours-${e.user_id}`}>{hrs(e.total_minutes)}</td>
        <td className="px-3 py-2 text-right text-[11px] text-muted-foreground">{open ? "Hide" : "Detail"}</td>
      </tr>
      {open && e.rows.map((r) => (
        <tr key={`${r.entity_id}-${r.arrived_at}`} className="border-t border-border/60 bg-muted/10 text-xs" data-testid={`${testId}-row-${r.entity_id}`}>
          <td className="px-3 py-1.5 pl-9">
            <span className="text-muted-foreground">{r.date}</span> · <Link to={jobLink(r)} className="text-primary hover:underline inline-flex items-center gap-0.5">{r.reference} <ArrowSquareOut size={10} /></Link> {r.title}
            <span className="block text-[10px] text-muted-foreground">{r.address}</span>
          </td>
          <td className="px-3 py-1.5 text-right text-muted-foreground" colSpan={2}>
            <span className="inline-flex items-center gap-1"><Clock size={10} /> {r.arrived} → {r.completed || <em>on site</em>}</span>
            {r.off_site && <span className="ml-1 text-amber-700" title={r.distance_m ? `${r.distance_m}m from site` : "off-site"}><Warning size={10} weight="fill" className="inline" /></span>}
          </td>
          <td className="px-3 py-1.5 text-right font-mono">{hrs(r.minutes)}</td>
          <td />
        </tr>
      ))}
    </>
  );
}
